import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from './components/PrivateRoute';
import PremiumRoute from './components/PremiumRoute';
import ThemeProvider from './components/ThemeProvider';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import ViewProfile from './pages/ViewProfile';
import Settings from './pages/Settings';
import Expense from './pages/Expense';
import Recurring from './pages/Recurring';
import Budgets from './pages/Budgets';
import IncomeTracking from './pages/IncomeTracking';
import Statement from './pages/Statement';
import CommunityForum from './pages/CommunityForum';
import ForumAgreementPage from './pages/ForumAgreementPage';
import CommunityForumInfo from './pages/CommunityForumInfo';
import ReportedZone from './components/CommunityForum/ReportedZone';
import Notification from './pages/Notification';
import Tutorial from './pages/Tutorial';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import IntelliSpend from './pages/IntelliSpend';
import BusinessInventory from './pages/BusinessInventory';
import BusinessProfile from './pages/BusinessProfile';
import Help from './components/Help';
import './App.css';
import { useState, useEffect, createContext } from 'react';
import { auth, db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';

// Create Development Mode Context
export const DevModeContext = createContext();

// ScrollToTop component to scroll window to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isDevMode, setIsDevMode] = useState(false);

  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribe = auth.onAuthStateChanged((user) => {
      setAuthInitialized(true);
      // console.log('Auth state changed:', user?.uid); // Debug log

      // Clean up previous listener if exists
      if (unsubscribeUser) {
        unsubscribeUser();
      }

      // If user is logged in, set up suspension monitoring
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        // console.log('Setting up suspension listener for:', user.uid); // Debug log

        unsubscribeUser = onSnapshot(userRef, (snapshot) => {
          // console.log('User document updated:', snapshot.data()); // Debug log
          
          if (snapshot.exists()) {
            const userData = snapshot.data();
            // console.log('User status:', userData.status); // Debug log

            if (userData.status === 'suspended') {
              // console.log('User is suspended, logging out...'); // Debug log
              
              // Force sign out
              signOut(auth).then(() => {
                // console.log('Logout successful'); // Debug log
                
                // Show toast notification
                toast.error('Your account has been suspended. Please contact support for assistance.', {
                  autoClose: false,
                  toastId: 'suspension-notice' // Prevent duplicate toasts
                });
                
                // Clear theme
                document.documentElement.className = '';
                
                // Force navigation to login page
                window.location.href = '/login';
              }).catch((error) => {
                console.error('Error signing out:', error);
              });
            }
          }
        }, (error) => {
          console.error('Error monitoring user status:', error);
        });
      }
    });

    // Detect if we're in development mode
    const isLocalhost = window.location.hostname === 'localhost' 
      || window.location.hostname === '127.0.0.1'
      || window.location.hostname.includes('192.168.');
    
    setIsDevMode(isLocalhost);

    return () => {
      unsubscribe();
      if (unsubscribeUser) {
        unsubscribeUser();
      }
    };
  }, []);

  // Show loading state while auth is initializing
  if (!authInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <AuthProvider>
      <ImpersonationProvider>
        <DevModeContext.Provider value={isDevMode}>
          <Router>
            <ScrollToTop />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Protected routes with theme */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Dashboard />
                  </ThemeProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Settings />
                  </ThemeProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <AdminDashboard />
                  </ThemeProvider>
                </PrivateRoute>
              }
            />
            <Route 
              path="/profile" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <ViewProfile />
                  </ThemeProvider>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/expense" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Expense />
                  </ThemeProvider>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/recurring" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Recurring />
                  </ThemeProvider>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/budgets" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Budgets />
                  </ThemeProvider>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/income" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <IncomeTracking />
                  </ThemeProvider>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/statement" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Statement />
                  </ThemeProvider>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/notifications" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Notification />
                  </ThemeProvider>
                </PrivateRoute>
              }
            />
            <Route 
              path="/tutorial" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Tutorial />
                  </ThemeProvider>
                </PrivateRoute>
              }
            />
            <Route 
              path="/help" 
              element={
                <PrivateRoute>
                  <ThemeProvider>
                    <Help />
                  </ThemeProvider>
                </PrivateRoute>
              }
            />
            <Route 
              path="/community" 
              element={
                <PrivateRoute>
                  <PremiumRoute>
                    <ThemeProvider>
                      <CommunityForum />
                    </ThemeProvider>
                  </PremiumRoute>
                </PrivateRoute>
              }
            />
            <Route 
              path="/forum/agreement" 
              element={
                <PrivateRoute>
                  <PremiumRoute>
                    <ForumAgreementPage />
                  </PremiumRoute>
                </PrivateRoute>
              }
            />
            <Route 
              path="/forum/reported" 
              element={
                <PrivateRoute>
                  <PremiumRoute>
                    <ThemeProvider>
                      <ReportedZone />
                    </ThemeProvider>
                  </PremiumRoute>
                </PrivateRoute>
              }
            />
            <Route 
              path="/intelliSpend" 
              element={
                <PrivateRoute>
                  <PremiumRoute>
                    <ThemeProvider>
                      <IntelliSpend />
                    </ThemeProvider>
                  </PremiumRoute>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/business-inventory" 
              element={
                <PrivateRoute>
                  <PremiumRoute>
                    <ThemeProvider>
                      <BusinessInventory />
                    </ThemeProvider>
                  </PremiumRoute>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/business-inventory/:profileId" 
              element={
                <PrivateRoute>
                  <PremiumRoute>
                    <ThemeProvider>
                      <BusinessProfile />
                    </ThemeProvider>
                  </PremiumRoute>
                </PrivateRoute>
              } 
            />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/community-info" element={<CommunityForumInfo />} />
          </Routes>
        </Router>
      </DevModeContext.Provider>
      </ImpersonationProvider>
    </AuthProvider>
  );
}

export default App;